import * as amplitude from '@amplitude/analytics-browser';

/**
 * Builds a regex that matches every URL *except* the provided routes.
 * The negative lookahead (?!...) inverts the match, so passing this to
 * pageUrlAllowlist effectively blacklists those routes from autocapture.
 *
 * @param {string[]} excludedRoutes - Array of route paths to exclude (e.g. ['/identity/login'])
 * @returns {RegExp}
 */
export function buildAutocaptureAllowlistRegex(excludedRoutes) {
	const routePattern = excludedRoutes
		.map(route => route.replace(/^\//, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
		.join('|');
	const regexString = `^https?://[^/]+/(?!(${routePattern})([/?]|$)).*`;
	return new RegExp(regexString);
}

/**
 * Sets the anonymous, hashed user ID in Amplitude for an authenticated member.
 * Call this after successfully hashing the member's stable identifier.
 *
 * @param {string} hashedId - A SHA256 hex-encoded identifier (never the raw member ID)
 */
export function setAmplitudeUser(hashedId) {
	amplitude.setUserId(hashedId);
}

/**
 * Clears the Amplitude user identity. Call this on logout.
 * Resets the device ID and user ID so subsequent events are not attributed
 * to the previous session.
 */
export function clearAmplitudeUser() {
	amplitude.reset();
}

/**
 * Tracks an Amplitude event.
 *
 * @param {string} eventName - The name of the event to track
 * @param {object} [properties] - Optional event properties
 * @param {object} [eventOptions] - Optional Amplitude EventOptions (e.g. { insert_id })
 */
export function trackEvent(eventName, properties, eventOptions) {
	amplitude.track(eventName, properties, eventOptions);
}

/**
 * Returns a Promise that resolves with the new document.title once it differs
 * from `staleTitle`. Uses a MutationObserver on <head><title> so it resolves
 * exactly when react-helmet-async commits the new title — no polling, no
 * arbitrary delay. Falls back after 1 s so the event is never held forever on
 * pages that have no <PageSEO> (and therefore never change the title).
 *
 * @param {string} staleTitle - The title captured at navigation time
 * @returns {Promise<string>}
 */
function waitForTitleChange(staleTitle) {
	return new Promise((resolve) => {
		if (document.title !== staleTitle) {
			resolve(document.title);
			return;
		}

		const titleEl = document.querySelector('head > title');
		if (!titleEl) {
			resolve(document.title);
			return;
		}

		const observer = new MutationObserver(() => {
			if (document.title !== staleTitle) {
				observer.disconnect();
				resolve(document.title);
			}
		});

		observer.observe(titleEl, { childList: true });

		setTimeout(() => {
			observer.disconnect();
			resolve(document.title);
		}, 1000);
	});
}

/**
 * Amplitude enrichment plugin that fixes the page title captured by Autocapture.
 *
 * Amplitude's Autocapture intercepts history.pushState synchronously and reads
 * document.title before React has re-rendered the new page. This plugin
 * intercepts [Amplitude] Page Viewed events and waits for react-helmet-async
 * to commit the correct title before the event is sent to the server.
 *
 * All other autocapture events pass through immediately and are unaffected.
 */
export const pageViewTitlePlugin = {
	name: 'page-view-title-fix',
	type: 'enrichment',
	setup: async () => undefined,
	execute: async (event) => {
		if (event.event_type === '[Amplitude] Page Viewed') {
			const staleTitle = event.event_properties?.['[Amplitude] Page Title'];
			const correctedTitle = await waitForTitleChange(staleTitle);
			return {
				...event,
				event_properties: {
					...event.event_properties,
					'[Amplitude] Page Title': correctedTitle,
				},
			};
		}
		return event;
	},
};
